/**
 * LA PARTITA CONTRO IL BOT DEGLI SCACCHI — le regole pure (E7 di ADR-0193; Luca, I-561/I-562, 09/10: «procediamo con bot!»,
 * livelli «Scala Elo a scelta», orologio «Entrambe», «TMA e web insieme»). Le esegue anche il gate sotto Node: niente `@/`,
 * niente React, niente `import.meta.env`.
 *
 * ⚠️ IL CONTRATTO E' DI qchess-orcr E PUO' CAMBIARE (PRATICA.md del cantiere -11): per questo la forma sta qui, in UN posto,
 * coi test sulla fixture (`tests/fixtures/pratica-busta-vera.json`). Un cambio di forma e' una riga qui e un test.
 *
 * Cosa sta qui:
 *  · il CORPO di `POST /v1/games/chess/practice-matches` — `{ botElo, mode, color }`, validato PRIMA di partire: un'Elo fuori
 *    scala, un orologio o un colore fuori elenco non escono dal telefono;
 *  · la LETTURA della risposta — `{ matchId, color, botElo, mode, wsUrl, resumed }`, dal payload GIA' srotolato dalla busta
 *    (`capabilityPayloadOf(unwrapData(...))`, v. `lib/limulo/model.ts`: qui non si ricava la busta a mano). Un campo mancante o
 *    strano e' un errore DICHIARATO col nome del campo, mai un `/chess/match/undefined`;
 *  · gli ERRORI come chiavi di frase: ogni esito ha la sua, nessuno e' «errore generico».
 * ⛔ Il `wsUrl` si legge e si tipa ma NON si mostra e non si usa: il socket lo apre il tavolo, con un ticket suo (`useChessTavolo`).
 */
import { chessMatchIdValido } from './tavolo.ts';

export const PRATICA_ROUTE = '/v1/games/chess/practice-matches';

/** La rotta della schermata di scelta (`routes/chess/ChessBot.tsx`), a cui porta la tessera della Hub. */
export const CHESS_BOT_PATH = '/chess/bot';

/** La scala dell'Elo del bot (I-562: «Scala Elo a scelta»). Il passo e' quello della schermata; il server accetta ogni intero nel range. */
export const BOT_ELO_MIN = 400;
export const BOT_ELO_MAX = 2800;
export const BOT_ELO_PASSO = 100;
export const BOT_ELO_DEFAULT = 1200;

/** Gli orologi (I-562: «Entrambe»): senza orologio, Blitz 3+2, Rapid 10+5. Gli identificativi sono quelli del game host. */
export const PRATICA_MODI = ['chess-unlimited', 'chess-blitz-3-2', 'chess-rapid-10-5'] as const;
export type PraticaModo = (typeof PRATICA_MODI)[number];

/** Il colore che chi gioca CHIEDE. Quello che gli tocca lo dice la risposta (`Pratica.color`). */
export const PRATICA_COLORI = ['W', 'B', 'random'] as const;
export type PraticaColoreScelto = (typeof PRATICA_COLORI)[number];

export interface SceltePratica {
  botElo: number;
  mode: PraticaModo;
  color: PraticaColoreScelto;
}

/** I valori con cui si apre la schermata: Elo 1200, senza orologio, colore casuale (dispatch tma-orcr-41). */
export const SCELTE_DI_PARTENZA: SceltePratica = { botElo: BOT_ELO_DEFAULT, mode: 'chess-unlimited', color: 'random' };

/** Un'Elo valida: intero, nella scala, a passo 100. */
export const eloBotValido = (n: unknown): n is number =>
  typeof n === 'number' && Number.isInteger(n) && n >= BOT_ELO_MIN && n <= BOT_ELO_MAX && (n - BOT_ELO_MIN) % BOT_ELO_PASSO === 0;

const modoValido = (v: unknown): v is PraticaModo => (PRATICA_MODI as readonly unknown[]).includes(v);
const coloreScelto = (v: unknown): v is PraticaColoreScelto => (PRATICA_COLORI as readonly unknown[]).includes(v);

/**
 * Il corpo della POST, o `null` se una scelta non e' valida (allora non si chiama nessuno). Rende un oggetto NUOVO con le tre
 * chiavi e basta: niente campi in piu' che qualcuno abbia lasciato nell'oggetto delle scelte.
 */
export function praticaBodyOf(scelte: { botElo: unknown; mode: unknown; color: unknown }): SceltePratica | null {
  if (!eloBotValido(scelte.botElo) || !modoValido(scelte.mode) || !coloreScelto(scelte.color)) return null;
  return { botElo: scelte.botElo, mode: scelte.mode, color: scelte.color };
}

export interface Pratica {
  matchId: string;
  /** Il colore ASSEGNATO a chi gioca (la scelta `random` e' gia' risolta dal server). */
  color: 'W' | 'B';
  botElo: number;
  mode: PraticaModo;
  /** Si tipa e non si usa: v. l'intestazione. */
  wsUrl: string;
  /** `true` = c'era gia' una pratica viva: la si riprende e i parametri nuovi sono stati ignorati dal server. */
  resumed: boolean;
}

/** Il campo della risposta che manca o e' strano, o `busta` se non c'e' nemmeno un oggetto da leggere. */
export type PraticaCampo = 'busta' | 'matchId' | 'color' | 'botElo' | 'mode' | 'wsUrl' | 'resumed';

export type PraticaRisposta = { ok: true; pratica: Pratica } | { ok: false; campo: PraticaCampo };

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/**
 * La pratica dal payload della capability. Si ferma al PRIMO campo che non va (l'ordine e' quello della risposta) e lo nomina.
 * `botElo` si legge come intero nella scala ma NON a passo 100: e' il server a dire che bot ha dato, e un'Elo "1250" e' vera.
 */
export function praticaRispostaOf(data: unknown): PraticaRisposta {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) return { ok: false, campo: 'busta' };
  const r = asRecord(data);
  if (typeof r.matchId !== 'string' || !chessMatchIdValido(r.matchId)) return { ok: false, campo: 'matchId' };
  if (r.color !== 'W' && r.color !== 'B') return { ok: false, campo: 'color' };
  const elo = r.botElo;
  if (typeof elo !== 'number' || !Number.isInteger(elo) || elo < BOT_ELO_MIN || elo > BOT_ELO_MAX) return { ok: false, campo: 'botElo' };
  if (!modoValido(r.mode)) return { ok: false, campo: 'mode' };
  if (typeof r.wsUrl !== 'string') return { ok: false, campo: 'wsUrl' };
  if (typeof r.resumed !== 'boolean') return { ok: false, campo: 'resumed' };
  return { ok: true, pratica: { matchId: r.matchId, color: r.color, botElo: elo, mode: r.mode, wsUrl: r.wsUrl, resumed: r.resumed } };
}

/** La rotta del tavolo di una pratica: la stessa di ogni partita di scacchi su Limulo (`chess/match/:id`). */
export { chessMatchPath as praticaTavoloPath } from './tavolo.ts';

// ─────────────────────────────────────────────────────────────────────────────
// Gli errori, come frasi (chiave sotto `errors.` nel namespace `chessBot`)
// ─────────────────────────────────────────────────────────────────────────────

export type ChiaveErrorePratica =
  /** 404 / 424 / app o capability spente: la rotta non e' servita (qui, oggi, finche' il router non e' acceso). */
  | 'nonServita'
  /** 403: il profilo non ha il permesso. */
  | 'nonAbilitata'
  /** 401: la sessione non vale piu'. */
  | 'sessione'
  /** La richiesta non e' arrivata. */
  | 'rete'
  /** Abbiamo smesso di aspettare noi. */
  | 'timeout'
  /** 400 / 422: il servizio ha rifiutato le scelte. */
  | 'scelte'
  /** 409: il servizio dice che c'e' gia' qualcosa in corso. */
  | 'giaInCorso'
  /** 429. */
  | 'troppeRichieste'
  /** 5xx. */
  | 'server'
  /** Un 200 che non ha la forma attesa (v. `PraticaCampo`). */
  | 'rispostaStrana'
  /** Qualunque altro esito: la frase porta lo status nudo. */
  | 'inatteso';

/**
 * Da un fallimento Limulo (`limuloFailure`: `status` e `verdict`) alla chiave della frase. Lo status vince sul verdetto dove e'
 * univoco; il verdetto decide dove lo stesso status ha piu' significati (403: permesso mancante o interruttore spento).
 */
export function chiaveErrorePratica(fallimento: { status: number | null; verdict: string }): ChiaveErrorePratica {
  const { status, verdict } = fallimento;
  if (status === null) return verdict === 'timeout' ? 'timeout' : verdict === 'network' ? 'rete' : 'inatteso';
  if (verdict === 'app-off' || verdict === 'capability-off' || verdict === 'switch-off') return 'nonServita';
  if (status === 401) return 'sessione';
  if (status === 403) return 'nonAbilitata';
  if (status === 404 || status === 424 || status === 501) return 'nonServita';
  if (status === 400 || status === 422) return 'scelte';
  if (status === 409) return 'giaInCorso';
  if (status === 429) return 'troppeRichieste';
  if (status >= 500) return 'server';
  return 'inatteso';
}
